import { IBotData, IPerformanceDataPoint } from "../shared/types";
import { isTreasuryBuyback } from "../utils/botMetrics";

/** One UTC day of the treasury chart (BuybacksChart). */
export interface IBuybackDataPoint {
  /** The day's UTC midnight (ms). */
  date: number;
  /** Tokens bought that day. */
  amount: number;
  /** Tokens bought up to and including that day. */
  cumulativeAmount: number;
}

const DAY_MS = 86_400_000;
const utcDay = (ts: number) => Math.floor(ts / DAY_MS) * DAY_MS;
const validTime = (ts?: number) => (typeof ts === "number" && Number.isFinite(ts) && ts > 0 ? ts : undefined);

/**
 * Treasury growth: tokens of the bots' own pair bought by their buy tasks (the backend's rule, see
 * isTreasuryBuyback), as a cumulative daily series in UTC from the first buyback day to today.
 * Each task's amount is spread evenly over the days it was buying, from createdAt (lastUpdatedAt
 * when missing) to lastUpdatedAt.
 */
export const processBuybackData = (
  bots: IBotData[],
  // No longer used: the window comes from the buybacks themselves. Kept so callers still compile.
  _performanceData?: IPerformanceDataPoint[]
): IBuybackDataPoint[] => {
  const daily = new Map<number, number>();
  let firstDay = Infinity;
  let lastDay = utcDay(Date.now());

  (bots || []).forEach((bot) => {
    (bot.buybacks || []).forEach((buyback) => {
      if (!bot.stats || !isTreasuryBuyback(bot.stats, buyback)) return;
      const amount = Number(buyback.totalBaseBought) || 0;
      const end = validTime(buyback.lastUpdatedAt) ?? validTime(buyback.createdAt);
      if (!(amount > 0) || end === undefined) return;

      const from = utcDay(Math.min(validTime(buyback.createdAt) ?? end, end));
      const to = utcDay(end);
      const perDay = amount / ((to - from) / DAY_MS + 1);
      for (let day = from; day <= to; day += DAY_MS) {
        daily.set(day, (daily.get(day) || 0) + perDay);
      }
      firstDay = Math.min(firstDay, from);
      lastDay = Math.max(lastDay, to);
    });
  });

  if (!Number.isFinite(firstDay)) return [];

  const series: IBuybackDataPoint[] = [];
  let cumulativeAmount = 0;
  for (let day = firstDay; day <= lastDay; day += DAY_MS) {
    const amount = daily.get(day) || 0;
    cumulativeAmount += amount;
    series.push({ date: day, amount, cumulativeAmount });
  }
  return series;
};
